import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { WebsiteSelectorComponent } from '../../components/website-selector/website-selector.component';
import { WebsiteService, SupportedLanguage, LanguageOption } from '../../services/website.service';
import { NotificationService } from '../../services/notification.service';
import { NotificationComponent } from '../../components/notification/notification.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-pages',
  standalone: true,
  imports: [
    CommonModule,
    WebsiteSelectorComponent,
    NotificationComponent
  ],
  templateUrl: './pages.component.html'
})
export class PagesComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  currentLocale: SupportedLanguage = 'en';
  availableLanguages: LanguageOption[] = [];

  constructor(
    private router: Router,
    private websiteService: WebsiteService,
    private notificationService: NotificationService
  ) {}

  async ngOnInit() {
    // Subscribe to global language changes
    this.websiteService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => {
        this.currentLocale = lang;
      });

    // Subscribe to domain changes
    this.websiteService.domainChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        await this.loadWebsiteSettings();
      });

    await this.loadWebsiteSettings();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadWebsiteSettings(): Promise<void> {
    const currentDomain = this.websiteService.getCurrentDomain();
    if (!currentDomain) return;

    const settings = await this.websiteService.getWebsiteSettings(currentDomain);
    if (settings) {
      this.availableLanguages = settings.availableLanguages || [];

      // Alleen de default taal instellen als de huidige taal niet beschikbaar is voor dit domein
      const availableCodes = this.availableLanguages.map(l => l.code);
      const storedLang = this.websiteService.getCurrentLanguage();
      if (!availableCodes.includes(storedLang) && settings.defaultLanguage) {
        const defaultLang = settings.defaultLanguage as SupportedLanguage;
        this.currentLocale = defaultLang;
        this.websiteService.setCurrentLanguage(defaultLang);
      }
    }
  }

  setCurrentLanguage(code: string) {
    this.currentLocale = code as SupportedLanguage;
    this.websiteService.setCurrentLanguage(this.currentLocale);
    
    // Get language name for feedback
    const language = this.availableLanguages.find(lang => lang.code === code);
    const languageName = language?.name || code.toUpperCase();
    
    this.notificationService.show({
      message: `Taal gewijzigd naar ${languageName}`,
      type: 'success'
    });
  }
}
